import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { FiChevronDown, FiChevronRight, FiUpload, FiPaperclip, FiExternalLink, FiDownload } from "react-icons/fi"

import useT from "utils/hooks/useT"
import { openPresignedFile } from "utils/s3Download"
import { ConfigContainer, Popup } from "components"
import { Container, Title, Text, Select } from "components/Form"
import DocumentUpload from "components/Lims/DocumentUpload"
import PendingApprovalLink from "components/Lims/ChangeControlLink"
import { limsForskrifter, message } from "state_management"
import { selectCanAuthorMasterData } from "state_management/permissions"
import { FORSKRIFT_STATES } from "state_management/lims/limsForskrifter"

// Analysis documents register (SPEC-014, PRD-004) — the LIMS "Analysis documents" workspace
// screen. ANALYSIS-FIRST: it lists every analysis definition (from state.analysisDefinitions);
// each row expands to the controlled method documents ATTACHED TO THAT ANALYSIS (the existing
// many-to-many link, doc.analysisDefinitionIds), with version history and its approval state, an
// "Upload new version" action and an "Attach document" action (attach an existing document, or
// create a new one already linked to this analysis). SPEC-058 B / BE SPEC-059: the authoring
// actions are PLANNER-only (selectCanAuthorMasterData — the server 403s anyone else; until then any
// LIMS member could upload), and the reviewer→supervisor APPROVAL is no longer signed here — a
// pending version points to LIMS › Change control, the one place master data is approved.
//
// This is a VIEW inversion + rename only: the data model and slice (limsForskrifter) are
// unchanged. The whole LimsScreen is already gated behind showLims so no extra flag check is
// needed here.
export default function AnalysisDocumentsRegister() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const canAuthor = useSelector(selectCanAuthorMasterData)

  const documents = useSelector(limsForskrifter.selectDocuments)
  const definitions = useSelector((state) => state.analysisDefinitions)

  const [expandedId, setExpandedId] = useState(null)

  useEffect(() => {
    // SPEC-021 (B3): QC capabilities ride on the member dump (loaded app-wide) — no fetch here.
    dispatch(limsForskrifter.fetch())
  }, [dispatch])

  // All analysis definitions, name-sorted — one row each.
  const analyses = useMemo(
    () =>
      Object.values(definitions || {})
        .filter((d) => d && d.name)
        .map((d) => ({ id: d.id, name: d.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [definitions]
  )

  // Map defId -> attached documents, derived from the many-to-many links on the documents.
  const documentsByDefinition = useMemo(() => {
    const byDef = {}
    documents.forEach((doc) => {
      const defIds = doc.analysisDefinitionIds || []
      defIds.forEach((defId) => {
        if (!byDef[defId]) byDef[defId] = []
        byDef[defId].push(doc)
      })
    })
    return byDef
  }, [documents])

  return (
    <div className="LimsForskrifter">
      <div className="LimsForskrifter-header">
        <h1 className="LimsForskrifter-title">{t("limsForskrifter")}</h1>
      </div>

      {analyses.length === 0 ? (
        <p className="LimsForskrifter-empty">{t("limsAnalysisDocNoAnalyses")}</p>
      ) : (
        <Table striped hover responsive className="LimsForskrifter-table">
          <thead>
            <tr>
              <th></th>
              <th>{t("limsAnalysisDocAnalysis")}</th>
              <th>{t("limsAnalysisDocDocuments")}</th>
              <th>{t("limsForskriftEffectiveVersion")}</th>
            </tr>
          </thead>
          <tbody>
            {analyses.map((analysis) => {
              const attached = documentsByDefinition[analysis.id] || []
              const expanded = expandedId === analysis.id
              return (
                <AnalysisRow
                  canAuthor={canAuthor}
                  key={analysis.id}
                  analysis={analysis}
                  attached={attached}
                  expanded={expanded}
                  onToggle={() => setExpandedId(expanded ? null : analysis.id)}
                  t={t}
                />
              )
            })}
          </tbody>
        </Table>
      )}
    </div>
  )
}

// One analysis master row plus, when expanded, an inline detail row listing the documents
// attached to this analysis (each with its version history + approval actions) and the attach
// action. Effective-version column summarises the effective version across attached documents.
function AnalysisRow({ analysis, attached, expanded, onToggle, canAuthor, t }) {
  const effectiveDoc = attached.find((doc) =>
    doc.versions.some((v) => v.state === FORSKRIFT_STATES.EFFECTIVE)
  )
  const effective =
    effectiveDoc && effectiveDoc.versions.find((v) => v.state === FORSKRIFT_STATES.EFFECTIVE)
  // #4101: versions standing in the approval chain — `draft` awaits the reviewer, `reviewer-approved`
  // awaits the second signature that makes it effective. Nothing on this row said so, so a document
  // sitting there waiting for a signature was indistinguishable from one nobody had started, and the
  // only way to find it was to expand every analysis in turn.
  const pendingCount = attached.reduce(
    (n, doc) =>
      n +
      doc.versions.filter(
        (v) => v.state === FORSKRIFT_STATES.DRAFT || v.state === FORSKRIFT_STATES.REVIEWER_APPROVED
      ).length,
    0
  )

  return (
    <>
      <tr className="LimsForskrifter-row" onClick={onToggle}>
        <td className="LimsForskrifter-chevron">
          {expanded ? <FiChevronDown /> : <FiChevronRight />}
        </td>
        <td>{analysis.name}</td>
        <td>
          {attached.length > 0
            ? attached.map((d) => d.name).join(", ")
            : t("limsAnalysisDocNoneAttachedShort")}
        </td>
        <td>
          {/* #4101: the two badges are laid out as a flex row so they are spaced whether they sit
              side by side or wrap onto separate lines in a narrow column — a margin alone left them
              touching once wrapped. */}
          <div className="LimsForskrifter-status-group">
            {effective ? (
              <span className="LimsForskrifter-status is-effective">
                {effectiveDoc.name} · {t("limsForskriftVersion")} {effective.versionNumber}
              </span>
            ) : (
              <span className="LimsForskrifter-status is-none">{t("limsForskriftNoEffective")}</span>
            )}
            {/* Shown ALONGSIDE the effective-version status, not instead of it — a document can be
                live and still have a newer version waiting for its signature. */}
            {pendingCount > 0 && (
              <span
                className="LimsForskrifter-status is-pending"
                title={t("limsForskriftPendingApprovalHint")}
              >
                {t("limsForskriftPendingApproval", { n: pendingCount })}
              </span>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="LimsForskrifter-detail-row">
          <td colSpan={4}>
            <AnalysisDocuments analysis={analysis} attached={attached} canAuthor={canAuthor} t={t} />
          </td>
        </tr>
      )}
    </>
  )
}

// The documents attached to a single analysis, plus the attach action. Each attached document
// renders its own version panel (history + Reviewer/QA approval + upload-new-version). An
// analysis with no attached document shows an empty-state note.
function AnalysisDocuments({ analysis, attached, canAuthor, t }) {
  const [attaching, setAttaching] = useState(false)

  return (
    <div className="LimsForskrifter-versions">
      {attached.length === 0 ? (
        <p className="LimsForskrifter-help">{t("limsAnalysisDocNoneAttached")}</p>
      ) : (
        attached.map((doc) => <DocumentPanel key={doc.id} doc={doc} canAuthor={canAuthor} t={t} />)
      )}

      {canAuthor && (
        <div className="LimsForskrifter-attach">
          <Button variant="primary" size="sm" onClick={() => setAttaching(true)}>
            <FiPaperclip /> {t("limsAnalysisDocAttach")}
          </Button>
        </div>
      )}

      {attaching && (
        <AttachDocument analysis={analysis} attached={attached} onClose={() => setAttaching(false)} />
      )}
    </div>
  )
}

// One attached document: its name + the version history with approval actions and an
// "Upload new version" action.
function DocumentPanel({ doc, canAuthor, t }) {
  const dispatch = useDispatch()
  const [uploadingVersion, setUploadingVersion] = useState(false)
  const [pendingFile, setPendingFile] = useState(null)

  const handleNewVersion = () => {
    if (!pendingFile) return
    setUploadingVersion(true)
    dispatch(limsForskrifter.addVersion(doc.id, pendingFile))
      .then(() => {
        dispatch(message.success(t("limsForskriftVersionAdded", { name: doc.name })))
        setPendingFile(null)
      })
      .catch(() => dispatch(message.error(t("limsForskriftVersionAddFailed", { name: doc.name }))))
      .finally(() => setUploadingVersion(false))
  }

  return (
    <div className="LimsForskrifter-document">
      <div className="LimsForskrifter-document-name">{doc.name}</div>
      <Table size="sm" className="LimsForskrifter-versions-table">
        <thead>
          <tr>
            <th>{t("limsForskriftVersion")}</th>
            <th>{t("limsForskriftFile")}</th>
            <th>{t("limsForskriftStatus")}</th>
            <th>{t("limsForskriftReviewedBy")}</th>
            <th>{t("limsForskriftSupervisorBy")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {doc.versions.map((version) => (
            <VersionRow key={version.id} version={version} />
          ))}
        </tbody>
      </Table>

      {canAuthor && (
        <div className="LimsForskrifter-newversion">
          <DocumentUpload
            onFile={setPendingFile}
            currentFileName={pendingFile ? pendingFile.name : null}
            uploading={uploadingVersion}
          />
          <Button
            variant="primary"
            size="sm"
            disabled={!pendingFile || uploadingVersion}
            onClick={handleNewVersion}
          >
            <FiUpload /> {t("limsForskriftUploadNewVersion")}
          </Button>
        </div>
      )}
    </div>
  )
}

// One version row: number, file (open/download), status badge, signers + the approval actions.
function VersionRow({ version }) {
  const { t } = useT("common")
  const members = useSelector((state) => state.members)
  const initials = (sig) =>
    (sig && sig.memberId && members[sig.memberId] && members[sig.memberId].initials) || "—"

  return (
    <tr>
      <td>{version.versionNumber}</td>
      <td>
        <FileLink file={version.file} />
      </td>
      <td>
        <span className={"LimsForskrifter-status " + statusClass(version.state)}>
          {t(statusLabelKey(version.state))}
        </span>
      </td>
      <td>{version.reviewerSignature ? initials(version.reviewerSignature) : "—"}</td>
      <td>{version.supervisorSignature ? initials(version.supervisorSignature) : "—"}</td>
      <td className="LimsForskrifter-version-actions">
        <PendingApprovalLink state={version.state} itemId={version.id} />
      </td>
    </tr>
  )
}

// The file cell. The bytes live in S3 under the version's team-scoped key; the presigned GET url
// is resolved ON CLICK via limsForskrifter.getReadUrl (the SP-E download contract, SPEC-021 B6) —
// lazily, so a register listing many versions mints a url only for the one a user actually opens
// (urls expire in ~1h, so caching them would be pointless). `resolving` guards against a double
// click firing two requests.
function FileLink({ file }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const [resolving, setResolving] = useState(false)

  if (!file) return <span>—</span>
  if (!file.key) return <span className="LimsForskrifter-filename">{file.name}</span>

  const open = () => {
    if (resolving) return
    setResolving(true)
    dispatch(limsForskrifter.getReadUrl(file.key))
      .then((url) => openPresignedFile(url))
      .catch(() => dispatch(message.error(t("limsForskriftDownloadError"))))
      .finally(() => setResolving(false))
  }

  return (
    <span className="LimsForskrifter-filename">
      {file.name}
      <button
        type="button"
        className="LimsForskrifter-file-action"
        onClick={open}
        disabled={resolving}
        title={t("limsForskriftOpen")}
        aria-label={t("limsForskriftOpen")}
      >
        <FiExternalLink />
      </button>
      <button
        type="button"
        className="LimsForskrifter-file-action"
        onClick={open}
        disabled={resolving}
        title={t("limsForskriftDownload")}
        aria-label={t("limsForskriftDownload")}
      >
        <FiDownload />
      </button>
    </span>
  )
}

// Attach-document editor for one analysis. Two ways to attach a controlled method document to
// this analysis:
//   1. Link an EXISTING document — multi-select of documents not already attached here; persisted
//      via linkDefinitions (adds this analysis to each chosen document's analysisDefinitionIds).
//   2. Create a NEW document already linked to this analysis (name + file -> createDocument).
// Both keep the existing many-to-many model; a single document can govern several analyses.
function AttachDocument({ analysis, attached, onClose }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const allDocuments = useSelector(limsForskrifter.selectDocuments)

  const [mode, setMode] = useState("existing") // "existing" | "new"
  const [selectedDocIds, setSelectedDocIds] = useState([])
  const [name, setName] = useState("")
  const [file, setFile] = useState(null)
  const [saving, setSaving] = useState(false)

  // Documents not already attached to this analysis, name-sorted, for the link picker.
  const attachableCollection = useMemo(() => {
    const attachedIds = new Set(attached.map((d) => d.id))
    return allDocuments
      .filter((d) => !attachedIds.has(d.id))
      .map((d) => ({ id: d.id, name: d.name }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
  }, [allDocuments, attached])

  const linkExisting = () => {
    if (selectedDocIds.length === 0) {
      dispatch(message.error(t("limsAnalysisDocSelectRequired")))
      return false
    }
    setSaving(true)
    Promise.all(
      selectedDocIds.map((docId) => {
        const doc = allDocuments.find((d) => d.id === docId)
        const nextIds = [...(doc ? doc.analysisDefinitionIds : []), analysis.id]
        return dispatch(limsForskrifter.linkDefinitions(docId, nextIds))
      })
    )
      .then(() => {
        dispatch(message.success(t("limsAnalysisDocAttached", { name: analysis.name })))
        onClose()
      })
      .catch(() => dispatch(message.error(t("limsForskriftLinkSaveFailed"))))
      .finally(() => setSaving(false))
    return true
  }

  const createNew = () => {
    const trimmed = name.trim()
    if (!trimmed) {
      dispatch(message.error(t("limsForskriftNameRequired")))
      return false
    }
    if (!file) {
      dispatch(message.error(t("limsForskriftFileRequired")))
      return false
    }
    setSaving(true)
    dispatch(
      limsForskrifter.createDocument(
        { name: trimmed, analysisDefinitionIds: [analysis.id] },
        file
      )
    )
      .then(() => {
        dispatch(message.success(t("limsForskriftCreated", { name: trimmed })))
        onClose()
      })
      .catch(() => dispatch(message.error(t("limsForskriftCreateFailed", { name: trimmed }))))
      .finally(() => setSaving(false))
    return true
  }

  const handleSubmit = () => (mode === "existing" ? linkExisting() : createNew())

  return (
    <Popup nonScrollable onCancel={onClose}>
      <ConfigContainer
        title={t("limsAnalysisDocAttachTo", { name: analysis.name })}
        cancelHandler={onClose}
        submitHandler={handleSubmit}
      >
        <div className="LimsForskrifter-mode">
          <Button
            variant={mode === "existing" ? "primary" : "outline-primary"}
            size="sm"
            onClick={() => setMode("existing")}
          >
            {t("limsAnalysisDocLinkExisting")}
          </Button>
          <Button
            variant={mode === "new" ? "primary" : "outline-primary"}
            size="sm"
            onClick={() => setMode("new")}
          >
            {t("limsAnalysisDocCreateNew")}
          </Button>
        </div>

        {mode === "existing" ? (
          <Container className="LimsForskrifter-field">
            <Title>{t("limsAnalysisDocDocuments")}</Title>
            <Select
              isMulti
              collection={attachableCollection}
              value={selectedDocIds}
              onChange={(values) => setSelectedDocIds(values || [])}
              menuPlacement="bottom"
            />
            <p className="LimsForskrifter-help">{t("limsAnalysisDocLinkExistingHelp")}</p>
          </Container>
        ) : (
          <>
            <Container className="LimsForskrifter-field">
              <Title>
                {t("limsForskriftName")} <div className="red-text">*</div>
              </Title>
              <Text
                value={name}
                required
                onChange={setName}
                placeholder={t("limsForskriftNamePlaceholder")}
              />
            </Container>
            <Container className="LimsForskrifter-field">
              <Title>
                {t("limsForskriftFile")} <div className="red-text">*</div>
              </Title>
              <DocumentUpload
                onFile={setFile}
                currentFileName={file ? file.name : null}
                uploading={saving}
              />
            </Container>
          </>
        )}
      </ConfigContainer>
    </Popup>
  )
}

function statusLabelKey(state) {
  if (state === FORSKRIFT_STATES.REVIEWER_APPROVED) return "limsForskriftStatusReviewerApproved"
  if (state === FORSKRIFT_STATES.EFFECTIVE) return "limsForskriftStatusEffective"
  if (state === FORSKRIFT_STATES.SUPERSEDED) return "limsForskriftStatusSuperseded"
  return "limsForskriftStatusDraft"
}

function statusClass(state) {
  if (state === FORSKRIFT_STATES.EFFECTIVE) return "is-effective"
  if (state === FORSKRIFT_STATES.REVIEWER_APPROVED) return "is-reviewer"
  if (state === FORSKRIFT_STATES.SUPERSEDED) return "is-superseded"
  return "is-draft"
}
